import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { useSkillRun } from '../../hooks/useSkillRun';
import { Action, Card, ErrorNote } from '../../components/aa';
import type { RunOutcome } from '../../types/aai';

/*
 * The skill runner, drawn for the employee portal.
 *
 * Presentation only — the turn loop is useSkillRun, shared with the older
 * RunSkillModal, so a run behaves identically wherever it is drawn.
 *
 * It renders as a body, not a panel, so a drawer can switch into it without
 * stacking a second panel over the first. A person who opened a Quick Win and
 * then ran it is still in one place, and closing once puts them back on the
 * list they started from.
 *
 * With `onLogRun` the body also closes the vetting loop: after a turn has gone
 * through, the person says how it went right here. That report is what counts
 * toward sharing, and the server refuses it until a real turn has been sent,
 * so the buttons wait for one.
 */

const BUBBLE: Record<string, string> = {
  user: 'ml-auto bg-ink text-white',
  assistant: 'mr-auto bg-canvas-inset text-ink',
};

const OUTCOMES: { value: RunOutcome; label: string; cls: string }[] = [
  { value: 'worked', label: 'Worked', cls: 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' },
  { value: 'partial', label: 'Partly', cls: 'border-amber-300 text-amber-700 hover:bg-amber-50' },
  { value: 'failed', label: "Didn't work", cls: 'border-red-300 text-red-700 hover:bg-red-50' },
];

export const SkillRunBody: React.FC<{
  skillId: string;
  onLogRun?: (outcome: RunOutcome, note: string) => Promise<void>;
}> = ({ skillId, onLogRun }) => {
  const { messages, needsApproval, gmailEnabled, busy, error, send, approveTool } = useSkillRun(skillId);
  const [input, setInput] = useState('');
  const [logNote, setLogNote] = useState('');
  const [logging, setLogging] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  const [logged, setLogged] = useState<RunOutcome | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  const submit = async () => {
    const text = input;
    setInput('');
    await send(text);
  };

  const ranOnce = messages.some((m) => m.role === 'user');

  const logRun = async (outcome: RunOutcome) => {
    if (!onLogRun) return;
    setLogging(true);
    setLogError(null);
    try {
      await onLogRun(outcome, logNote.trim());
      setLogged(outcome);
      setLogNote('');
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setLogError(detail || 'Could not record that run.');
    } finally {
      setLogging(false);
    }
  };

  return (
    <>
      {error && <ErrorNote className="mb-4" message={error} />}

      {/* A tool the person chose to gate stays gated: the approval is asked
          for here, applies to this run only, and is never remembered. */}
      {needsApproval.includes('gmail_read') && (
        <Card className="mb-4" pad="md">
          <p className="m-0 text-[13px] leading-[21.5px] text-ink">
            This skill can read your Gmail. You chose “only with approval” in your tool dive, so
            it is asking now.
          </p>
          <Action className="mt-3" size="sm" onClick={() => approveTool('gmail_read')} disabled={busy} reason="Working…">
            Allow for this run only
          </Action>
        </Card>
      )}

      {gmailEnabled && (
        <p className="m-0 mb-4 text-[12.5px] leading-[20.75px] text-ink-dim">
          Reading your Gmail, read-only, for this run.
        </p>
      )}

      <div className="grid gap-3">
        {messages.map((message, i) => (
          <div
            key={`${message.role}-${i}`}
            className={`max-w-[85%] whitespace-pre-wrap rounded-card px-3.5 py-2.5 text-[13px] leading-[21.5px] ${BUBBLE[message.role]}`}
          >
            {message.content}
          </div>
        ))}
        {busy && (
          <div className="mr-auto rounded-card bg-canvas-inset px-3.5 py-2.5 text-ink-soft">
            <Loader2 size={14} className="animate-spin" />
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="mt-4 flex items-end gap-2.5">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
          rows={2}
          placeholder="Your input for this run…"
          aria-label="Your input for this run"
          className="min-h-[62px] w-full resize-y rounded-act border border-input bg-white p-3 text-[13px] leading-[21.5px] text-ink outline-none transition-colors placeholder:text-ink-soft focus:border-ink"
        />
        <Action
          onClick={submit}
          disabled={busy || !input.trim()}
          reason="Type something to send"
          icon={<Send size={14} />}
        >
          Send
        </Action>
      </div>

      <p className="m-0 mt-3 text-[12.5px] leading-[20.75px] text-ink-dim">
        This run is private to you. It becomes a shared result only if you share it, and a live
        result only once you review it.
      </p>

      {onLogRun && (
        <Card className="mt-5" pad="md">
          <p className="m-0 text-[13px] font-semibold leading-[21.5px] text-ink">Record this run</p>
          <p className="m-0 mt-1 text-[12.5px] leading-[20.75px] text-ink-dim">
            {ranOnce && 'How did it go? This is what counts toward sharing it with your team.'}
            {!ranOnce && 'Send the skill something real first. Recording opens once a run has gone through.'}
          </p>
          {logged && (
            <p className="m-0 mt-2 text-[12.5px] leading-[20.75px] text-ink">
              Recorded as {OUTCOMES.find((o) => o.value === logged)?.label.toLowerCase()}. Run it again to record another.
            </p>
          )}
          {logError && <ErrorNote className="mt-3" message={logError} />}
          <textarea
            value={logNote}
            onChange={(e) => setLogNote(e.target.value)}
            rows={2}
            disabled={!ranOnce || logging}
            placeholder="What happened? What to fix next?"
            aria-label="Notes on this run"
            className="mt-3 w-full resize-y rounded-act border border-input bg-white p-3 text-[13px] leading-[21.5px] text-ink outline-none transition-colors placeholder:text-ink-soft focus:border-ink disabled:cursor-not-allowed disabled:bg-canvas"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {OUTCOMES.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => logRun(o.value)}
                disabled={!ranOnce || logging || busy}
                className={`inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1.5 text-[12.5px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${o.cls} whitespace-nowrap`}
              >
                {logging && <Loader2 size={12} className="animate-spin" />}
                {o.label}
              </button>
            ))}
          </div>
        </Card>
      )}
    </>
  );
};
